import { action, computed, observable } from 'mobx'
import { SearchTextViewModel } from '@/infra/SearchTextViewModel'
import type { GetCurrentUserWorkspaceMembershipsFn } from '@/features/iam'
import { SelectFilter } from '@/infra/SelectFilter'
import type { CursorPageQuery } from '@/infra/paging'
import type { ActivationLifecycle } from '@/infra/activation'
import { LifecycleTracker } from '@/infra/lifecycle-tracker'
import { type Id } from '@/infra/id'
import {
  FiltersViewModel,
  FilterItemSource,
  type FilterStorage,
  type FilterItem,
} from '@/infra/FiltersViewModel'
import { SingleSelectFilter } from '@/infra/SingleSelectFilter'
import {
  type WorkspacePosting,
  type WorkspacePostingStatus,
} from '@/features/admin/staffing/WorkspacePosting'
import { InfiniteScrollProvider } from '@/infra/InfiniteScrollProvider'
import { type TableSortOrder } from '@/components/Table'
import { type SearchableStaffingSortField } from '@/features/admin/staffing/components/StaffingTable/staffing-table-columns'
import {
  type ListWorkspacePostingsQuery,
  type WorkspacePostingStore,
} from '@/features/admin/staffing/WorkspacePostingStore'
import {
  type Observable,
  catchError,
  combineLatest,
  distinctUntilChanged,
  EMPTY,
  map,
  switchMap,
  tap,
} from 'rxjs'
import { toStream, tapAction } from '@/utils/mobx-util'
import { shareLatest, withAbortSignal } from '@/utils/rx-util'
import { match } from '@/utils/pattern-match-util'
import { omit, uniq } from 'lodash-es'
import { deepEqual } from '@/utils/object-util'
import type { SelectionState } from '@/infra/selection-state'
import type { AlertRetryWrapperFactory } from '@/features/shell'

/**
 * The status groups the postings can be filtered by.
 */
export type WorkspacePostingStatusFilterOption =
  'all' | 'draft' | 'active' | 'inReview' | 'closed'

export interface WorkspacePostingSort {
  field: SearchableStaffingSortField
  direction: TableSortOrder
}

export interface WorkspacePostingStatusFilter {
  count?: number
  type: WorkspacePostingStatusFilterOption
}

const WorkspacePostingStatusFilter = {
  none: {
    count: 0,
    type: 'all',
  } as WorkspacePostingStatusFilter,
}

export interface WorkspacePostingFilter {
  workspaceIds: Id[]
  status: WorkspacePostingStatusFilter
}

export interface WorkspacePostingSearchFilter {
  workspaceIds: Id[]
  statuses: WorkspacePostingStatus[]
  sort: WorkspacePostingSort
}

export const WorkspacePostingSearchFilter = {
  /**
   * Empty search filter.
   */
  none: {
    workspaceIds: [],
    sort: { field: 'WorkspaceId', direction: 'none' },
    statuses: [],
  } as WorkspacePostingSearchFilter,

  /**
   * Whether search filter is empty.
   */
  isNone: (searchFilter: WorkspacePostingSearchFilter) =>
    WorkspacePostingSearchFilter.equals(
      omit(WorkspacePostingSearchFilter.none, 'sort'),
      omit(searchFilter, 'sort'),
    ),

  /**
   * Whether two search filters are equal.
   */
  equals: (
    left: Partial<WorkspacePostingSearchFilter>,
    right: Partial<WorkspacePostingSearchFilter>,
  ) => deepEqual(left, right),
}

export interface WorkspacePostingSearchResult {
  searchFilter: WorkspacePostingSearchFilter
  filtersApplied: boolean
  readonly workspacePostings: readonly WorkspacePosting[]
}

export class StaffingSearchViewModel implements ActivationLifecycle {
  private readonly _lifecycle = new LifecycleTracker()

  /**
   * Tracks details for the previous search.
   */
  private _lastSearch: WorkspacePostingSearchResult = {
    searchFilter: WorkspacePostingSearchFilter.none,
    filtersApplied: false,
    workspacePostings: [],
  }

  /**
   * Search filter pipeline.
   */
  private readonly _searches$

  readonly filters

  /**
   * Sorting.
   */
  @observable accessor sorting: WorkspacePostingSort = {
    field: 'WorkspaceId',
    direction: 'none',
  }

  /**
   * Search text, filtering the loaded postings.
   */
  readonly searchText = new SearchTextViewModel()

  /**
   * Staffing scroller.
   */
  readonly scroller = new InfiniteScrollProvider<
    WorkspacePosting,
    WorkspacePostingSearchFilter
  >({
    limit: 20,
    fetchItems: ({ paging, signal, filter }) => {
      const withRetry = this._createAlertRetryWrapper(
        'Loading postings failed',
        signal,
      )
      return withRetry(() =>
        this._workspacePostingStore.listWorkspacePostings(
          this._toListWorkspacePostingsQuery(paging, filter),
          signal,
        ),
      )
    },
  })

  /**
   * Collection of selected workspace postings.
   */
  @observable accessor selected = new Set<WorkspacePosting>()

  constructor(
    private readonly _workspacePostingStore: WorkspacePostingStore,
    private readonly _createAlertRetryWrapper: AlertRetryWrapperFactory,
    private readonly _getWorkspaceMemberships: GetCurrentUserWorkspaceMembershipsFn,
    private readonly _filterStorage?: FilterStorage,
  ) {
    this.filters = new FiltersViewModel<WorkspacePostingFilter>({
      filterStorage: this._filterStorage,
      definitions: {
        workspaceIds: {
          title: 'Workspaces',
          implicit: true,
          factory: (key, title) =>
            new SelectFilter(
              key,
              title,
              FilterItemSource.static(this.workspacesWithStaffingPermission),
              true,
            ),
        },
        status: {
          icon: 'pending',
          title: 'Quick Select',
          implicit: true,
          factory: (key, title) =>
            new SingleSelectFilter(
              title,
              key,
              WorkspacePostingStatusFilter.none,
              (signal) => this._fetchStatusOptions(signal),
            ),
        },
      },
    })

    this._searches$ = combineLatest([
      this.filters.appliedFilters$,
      toStream(() => this.sorting, true),
    ]).pipe(
      map(([appliedFilters, sort]): WorkspacePostingSearchFilter => ({
        workspaceIds: appliedFilters.workspaceIds ?? [],
        statuses: toWorkspacePostingStatus(appliedFilters.status?.type),
        sort,
      })),
      distinctUntilChanged<WorkspacePostingSearchFilter>(
        WorkspacePostingSearchFilter.equals,
      ),
      switchMap((query): Observable<WorkspacePostingSearchResult> =>
        withAbortSignal((signal) => this._searchItems(query, signal)).pipe(
          // The retry alert has reported the failure.
          catchError(() => EMPTY),
        ),
      ),
      switchMap(async (result) => {
        await this._fetchFilterOptions(this._lifecycle.signal)
        return result
      }),
      tap(() => this._cleanSelected()),
      shareLatest(),
    )

    this._lifecycle.manage(
      this._searches$.pipe(tapAction((search) => (this._lastSearch = search))),
    )
  }

  /**
   * Workspaces in which the user can manage staffing.
   */
  @computed
  get workspacesWithStaffingPermission(): FilterItem<Id>[] {
    return this._getWorkspaceMemberships()
      .filter(
        (mb) =>
          mb.workspace.config.staffing.enabled &&
          mb.member.permissions.adminStaffingManage,
      )
      .map((mbs) => {
        return { title: mbs.workspace.name, value: mbs.workspace.id }
      })
  }

  /**
   * Returns whether scroller items are all selected, none selected, or in between.
   */
  @computed
  get selectionState(): SelectionState {
    if (this.selected.size === 0) {
      return 'NoneSelected'
    }

    if (this.scroller.items.length === this.selected.size) {
      return 'AllSelected'
    }

    if (this.selected.size > 0) {
      return 'SomeSelected'
    }

    return 'NoneSelected'
  }

  /**
   * Returns workspace postings, as currently filtered.
   */
  @computed
  get workspacePostings() {
    return uniq(this.scroller.items)
  }

  /**
   * Returns workspace filter.
   */
  @computed
  get workspaceFilter() {
    return this.filters.getFilter('workspaceIds') as SelectFilter<string, Id>
  }

  /**
   * Returns status filter.
   */
  @computed
  get statusFilter() {
    return this.filters.getFilter('status') as SingleSelectFilter<
      string,
      WorkspacePostingStatusFilter
    >
  }

  /**
   * Loaded workspace postings matching the search text.
   */
  @computed
  get filteredItems() {
    if (!this.searchText.value) {
      return this.scroller.items
    }

    const text = this.searchText.value.toLowerCase()
    return this.scroller.items.filter(
      (wp) =>
        wp.workspace.name.toLocaleLowerCase().includes(text) ||
        wp.location.toLowerCase()?.includes(text) ||
        wp.primarySoftware.toLowerCase()?.includes(text) ||
        wp.supplementalSoftware.toLowerCase()?.includes(text),
    )
  }

  /**
   * Returns whether posting is currently selected.
   * @param posting
   */
  isSelected(posting: WorkspacePosting): boolean {
    return this.selected.has(posting)
  }

  /**
   * Toggles whether an individual posting is selected.
   */
  @action.bound
  togglePosting(posting: WorkspacePosting) {
    if (this.isSelected(posting)) {
      this.selected.delete(posting)
    } else {
      this.selected.add(posting)
    }
  }

  /**
   * Toggles whether all postings are selected.
   */
  @action.bound
  toggleAllPostings() {
    switch (this.selectionState) {
      case 'NoneSelected':
      case 'SomeSelected':
        this.scroller.items.forEach((posting) => {
          this.selected.add(posting)
        })
        break
      case 'AllSelected':
        this.clearSelected()
        break
    }
  }

  /**
   * Removes all selected postings.
   */
  @action.bound
  clearSelected() {
    this.selected.clear()
  }

  /**
   * Sets the staffing sort.
   * @param sortMethod
   */
  @action.bound
  setSortMethod(sortMethod: WorkspacePostingSort) {
    this.sorting = sortMethod
  }

  /**
   * Sets the staffing sort direction in particular.
   * @param direction
   */
  @action.bound
  setSortDirection(direction: TableSortOrder) {
    // If we have no direction remove any sorting.
    if (direction === 'none') {
      this.setSortMethod({ field: 'WorkspaceId', direction: 'none' })
      return
    }

    this.setSortMethod({
      direction,
      field: this.sorting.field,
    })
  }

  /**
   * Sets the staffing sort field in particular.
   * @param field
   */
  @action.bound
  setSortField(field: SearchableStaffingSortField) {
    this.setSortMethod({
      field,
      direction: this.sorting.direction,
    })
  }

  /**
   * Re-runs the last search and reloads the status counts.
   */
  @action.bound
  async refreshStatusFilter() {
    const signal = this._lifecycle.signal
    // The retry alert has reported a failed search.
    await this._searchItems(this._lastSearch.searchFilter, signal).catch(
      () => undefined,
    )
    await this.statusFilter.fetchOptions(signal).catch(() => undefined)
  }

  /** @inheritDoc */
  activate() {
    // The filters restore their stored values on activation, which has to
    // happen before the searches subscribe; the lifecycle tracker would
    // subscribe the searches first.
    this.filters.activate()
    this._lifecycle.activate()
  }

  /** @inheritDoc */
  @action.bound
  deactivate() {
    this.searchText.clear()
    this.filters.deactivate()
    this._lifecycle.deactivate()
  }

  /**
   * Fetches the filter options. A failure leaves the previous options, the
   * retry alert having reported it.
   */
  private async _fetchFilterOptions(signal: AbortSignal) {
    await this.workspaceFilter.fetchOptions(signal).catch(() => undefined)
    await this.statusFilter.fetchOptions(signal).catch(() => undefined)
  }

  /**
   * Runs a search with a query, and returns the query and filters used, as well as the postings themselves.
   * @param query
   * @param signal
   */
  private async _searchItems(
    query: WorkspacePostingSearchFilter,
    signal?: AbortSignal,
  ) {
    await this.scroller.fetch({ filter: query, signal })

    while (this.workspacePostings.length === 0 && !this.scroller.isDone) {
      signal?.throwIfAborted()

      await this.scroller.more(signal)
    }

    return {
      searchFilter: query,
      filtersApplied: !WorkspacePostingSearchFilter.isNone(query),
      workspacePostings: this.workspacePostings,
    }
  }

  /**
   * Status list for status filter. Posting aggregations queried for number of postings per status filter option.
   */
  private async _fetchStatusOptions(
    signal?: AbortSignal,
  ): Promise<FilterItem<WorkspacePostingStatusFilter>[]> {
    const allowedWorkspaces = this.workspacesWithStaffingPermission.map(
      (w) => w.value,
    )
    const queryWorkspaces = this.workspaceFilter.value || []
    const withRetry = this._createAlertRetryWrapper(
      'Loading posting counts failed',
      signal,
    )
    await withRetry(() =>
      this._workspacePostingStore.fetchAggregations(
        queryWorkspaces.length > 0 ? queryWorkspaces : allowedWorkspaces,
        signal,
      ),
    )

    return [
      {
        title: 'All',
        value: {
          type: 'all',
          count: this._getWorkspacePostingsByStatusCount('all'),
        },
      },
      {
        title: 'Draft',
        value: {
          type: 'draft',
          count: this._getWorkspacePostingsByStatusCount('draft'),
        },
      },
      {
        title: 'Active',
        value: {
          type: 'active',
          count: this._getWorkspacePostingsByStatusCount('active'),
        },
      },
      {
        title: 'In review',
        value: {
          type: 'inReview',
          count: this._getWorkspacePostingsByStatusCount('inReview'),
        },
      },
      {
        title: 'Closed',
        value: {
          type: 'closed',
          count: this._getWorkspacePostingsByStatusCount('closed'),
        },
      },
    ]
  }

  /**
   * Removes postings not present in current scroller items.
   */
  @action.bound
  private _cleanSelected() {
    for (const posting of this.selected) {
      if (!this.scroller.items.includes(posting)) {
        this.selected.delete(posting)
      }
    }
  }

  /**
   * Assembles filters and paging into the workspace posting query.
   * @param paging
   * @param filter
   */
  private _toListWorkspacePostingsQuery(
    paging: CursorPageQuery,
    filter?: WorkspacePostingSearchFilter,
  ): ListWorkspacePostingsQuery {
    const allowedWorkspaces = this.workspacesWithStaffingPermission.map(
      (w) => w.value,
    )
    const queryWorkspaces = filter?.workspaceIds ?? []

    return {
      workspaceIds:
        queryWorkspaces.length > 0 ? queryWorkspaces : allowedWorkspaces,
      statuses: filter?.statuses ?? [],
      limit: paging.limit,
      after: paging.after,
    }
  }

  /**
   * Workspace postings filtered by status.
   * @param status
   */
  private _getWorkspacePostingsByStatusCount(
    status: WorkspacePostingStatusFilterOption,
  ) {
    return this._workspacePostingStore.workspacePostingAggregations
      .filter((postingsByStatus) => {
        return toWorkspacePostingStatus(status).includes(
          postingsByStatus?.status,
        )
      })
      .reduce((acc, next) => acc + next.total, 0)
  }
}

/**
 * Returns workspace posting statuses relevant for a status filter option.
 * @param status
 */
function toWorkspacePostingStatus(
  status?: WorkspacePostingStatusFilterOption,
): WorkspacePostingStatus[] {
  if (!status) {
    return []
  }

  return match<WorkspacePostingStatusFilterOption, WorkspacePostingStatus[]>(
    status,
    {
      all: () => ['Active', 'Cancelled', 'Closed', 'Draft', 'InReview'],
      draft: () => ['Draft'],
      active: () => ['Active'],
      inReview: () => ['InReview'],
      closed: () => ['Cancelled', 'Closed'],
    },
  )
}
